import { useQuery } from "@apollo/client";
import {
  CheckCircle,
  ChevronRight,
  CircleNotifications,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  List,
  ListItem,
  Typography,
  useMediaQuery,
} from "@mui/material";
import _ from "lodash";
import { useSnackbar } from "notistack";
import { ReactNode, useCallback, useEffect, useState } from "react";
import { ReactElement } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";

import {
  CampaignStatus,
  EnqueueTexterSendMutation,
  EnqueueTexterSendMutationVariables,
  InboxType,
  ListMyInboxesQuery,
  ListMyInboxesQueryVariables,
  ReleaseAssignmentsMutation,
  ReleaseAssignmentsMutationVariables,
  RequestAssignmentBatchMutation,
  RequestAssignmentBatchMutationVariables,
  TexterCampaignSendStatus,
} from "~frontend/__generated__/graphql";
import ErrorCard from "~frontend/components/ErrorCard";
import { FlexBox } from "~frontend/components/FlexBox";
import { LinkButton } from "~frontend/components/Link";
import Loading from "~frontend/components/Loading";
import PageContainer from "~frontend/components/PageContainer";
import Table, {
  TablePropsWithCustomFooter,
  TablePropsWithoutFooter,
  TablePropsWithPaginationFooter,
} from "~frontend/components/Table";
import {
  RELEASE_ASSIGNMENTS,
  REQUEST_ASSIGNMENT_BATCH,
} from "~frontend/graphql/assignments";
import { ENQUEUE_TEXTER_SEND } from "~frontend/graphql/campaigns";
import { LIST_MY_INBOXES } from "~frontend/graphql/inboxes";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import { useIntl } from "~frontend/providers/IntlProvider";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import theme, { palette } from "~frontend/theme";
import { formatDateTime } from "~frontend/util/dateFormat";
import useMutation from "~frontend/util/useMutation";

import { InboxCampaignSummary } from "./InboxCampaignSummary";
import { CampaignInboxReplyControls } from "./ReplyControls";
import { CampaignInboxSummary } from "./types";

export function InboxList(): ReactElement {
  const snackbar = useSnackbar();
  const intl = useIntl();
  const { subaccount, subaccountLink } = useSubaccount()!;
  const navigate = useNavigate();
  const isNarrow = useMediaQuery(theme.breakpoints.down("wide"));

  useBreadcrumbs(
    [
      {
        label: `${subaccount.name}`,
        path: subaccountLink("/inboxes"),
        backArrow: false,
      },
      {
        label: `Texter Dashboard`,
        path: subaccountLink("/inboxes"),
        backArrow: false,
      },
    ],
    { hideSubaccountTitle: true }
  );

  const goToInbox = useCallback(
    ({ inboxId }: { inboxId: string }) =>
      navigate(subaccountLink(`/inboxes/${inboxId}`)),
    [navigate, subaccountLink]
  );

  const { loading, error, data, startPolling, stopPolling } = useQuery<
    ListMyInboxesQuery,
    ListMyInboxesQueryVariables
  >(LIST_MY_INBOXES, {
    variables: {
      input: { subaccountId: subaccount.id },
    },
  });

  const [inboxesUpdatedAt, setInboxesUpdatedAt] = useState<Date | null>(null);

  useEffect(() => {
    if (data) {
      setInboxesUpdatedAt(new Date());
    }
  }, [data, setInboxesUpdatedAt]);

  useEffect(() => {
    const hasPendingCampaignSend = data?.listMyInboxes
      .flatMap(inbox =>
        inbox.__typename === "CampaignInboxSummary"
          ? inbox.campaign.myCampaignSends
          : []
      )
      .some(send => send.status === TexterCampaignSendStatus.Pending);

    if (hasPendingCampaignSend) {
      startPolling(1000);
    } else {
      startPolling(60 * 1000);
    }

    return () => stopPolling();
  }, [data, startPolling, stopPolling]);

  const [requestAssignmentBatch, { loading: requestAssignmentBatchLoading }] =
    useMutation<
      RequestAssignmentBatchMutation,
      RequestAssignmentBatchMutationVariables
    >(REQUEST_ASSIGNMENT_BATCH, {
      refetchQueries: [LIST_MY_INBOXES],
      awaitRefetchQueries: true,
      onErrorSnackbarMessage: intl.formatMessage({
        defaultMessage: "Error claiming replies",
        description: "Error message for claiming replies",
      }),
    });

  const [releaseAssignments, { loading: releaseAssignmentsLoading }] =
    useMutation<
      ReleaseAssignmentsMutation,
      ReleaseAssignmentsMutationVariables
    >(RELEASE_ASSIGNMENTS, {
      refetchQueries: [LIST_MY_INBOXES],
      awaitRefetchQueries: true,
      onErrorSnackbarMessage: intl.formatMessage({
        defaultMessage: "Error releasing replies",
        description: "Error message for releasing replies",
      }),
    });

  const assignmentRequestInProgress =
    requestAssignmentBatchLoading || releaseAssignmentsLoading;

  const handleRequestAssignmentBatch = useCallback(
    async ({ inboxId }: { inboxId: string }) => {
      if (assignmentRequestInProgress) {
        return;
      }

      const requestAssignmentBatchResponse = await requestAssignmentBatch({
        variables: { input: { inboxId } },
      });

      if (
        requestAssignmentBatchResponse.errors ||
        !requestAssignmentBatchResponse.data
      ) {
        console.error(requestAssignmentBatchResponse.errors);
        snackbar.enqueueSnackbar(
          intl.formatMessage({
            defaultMessage: "Error claiming replies",
            description: "Error message for claiming replies",
          })
        );
        return;
      }

      goToInbox({ inboxId })?.catch(console.error);
    },
    [
      assignmentRequestInProgress,
      requestAssignmentBatch,
      snackbar,
      intl,
      goToInbox,
    ]
  );

  const handleReleaseAssignments = useCallback(
    async ({ inboxId }: { inboxId?: string } = {}) => {
      if (assignmentRequestInProgress) {
        return;
      }

      const releaseAssignmentsResponse = await releaseAssignments({
        variables: { input: { subaccountId: subaccount.id, inboxId } },
      });

      if (
        releaseAssignmentsResponse.errors ||
        !releaseAssignmentsResponse.data
      ) {
        console.error(releaseAssignmentsResponse.errors);
        snackbar.enqueueSnackbar(
          intl.formatMessage({
            defaultMessage: "Error releasing replies",
            description: "Error message for releasing replies",
          })
        );
        return;
      }
    },
    [
      assignmentRequestInProgress,
      releaseAssignments,
      subaccount,
      snackbar,
      intl,
    ]
  );

  const [
    enqueuingTexterSendForCampaignId,
    setEnqueuingTexterSendForCampaignId,
  ] = useState<string | null>(null);

  const [enqueueTexterSend] = useMutation<
    EnqueueTexterSendMutation,
    EnqueueTexterSendMutationVariables
  >(ENQUEUE_TEXTER_SEND, {
    refetchQueries: [LIST_MY_INBOXES],
    awaitRefetchQueries: true,
    onErrorSnackbarMessage: intl.formatMessage({
      defaultMessage: "Error sending initials",
      description: "Error message for sending initial messages",
    }),
  });

  const handleEnqueueTexterSend = useCallback(
    async (campaignId: string) => {
      if (enqueuingTexterSendForCampaignId) {
        return;
      }

      setEnqueuingTexterSendForCampaignId(campaignId);

      try {
        const { data, errors } = await enqueueTexterSend({
          variables: { input: { campaignId } },
        });

        if (errors || !data) {
          snackbar.enqueueSnackbar(
            intl.formatMessage({
              defaultMessage: "Error sending initials",
              description: "Error message for sending initial messages",
            })
          );
        }
      } finally {
        setEnqueuingTexterSendForCampaignId(null);
      }
    },
    [
      enqueuingTexterSendForCampaignId,
      setEnqueuingTexterSendForCampaignId,
      enqueueTexterSend,
      snackbar,
      intl,
    ]
  );

  if (loading) {
    return (
      <PageContainer>
        <Loading />
      </PageContainer>
    );
  }

  if (error) {
    return <ErrorCard errors={[error]} />;
  }

  const inboxes = data!.listMyInboxes;
  const escalationInboxes = inboxes.filter(
    inbox => inbox.__typename === "EscalationInboxSummary"
  );
  const generalInboxes = inboxes.filter(
    inbox => inbox.__typename === "GeneralInboxSummary"
  );
  const campaignInboxes: CampaignInboxSummary[] = inboxes
    .filter(inbox => inbox.__typename === "CampaignInboxSummary")
    .filter(inbox => inbox.campaign.status !== CampaignStatus.Archived);

  const totalCampaignAssignedReplies = _.sumBy(
    campaignInboxes,
    inbox => inbox.stats.numRepliesAssigned
  );

  return (
    <PageContainer maxWidth="md" role="main">
      <FlexBox direction="column" gap="24px">
        {escalationInboxes.map(inbox => (
          <InboxListSection
            key={`inbox-${inbox.id}`}
            title={inbox.inboxName}
            label={intl.formatMessage({
              defaultMessage: "Escalation",
              description: "Label for an escalation inbox",
            })}
            alerts={[
              {
                key: "available-escalations",
                text: intl.formatMessage(
                  {
                    defaultMessage:
                      "Available Escalations: {numRepliesAvailable}",
                    description:
                      "Alert for the number of available escalations in an inbox",
                  },
                  { numRepliesAvailable: inbox.stats.numRepliesAvailable }
                ),
              },
              {
                key: "your-escalations",
                text: intl.formatMessage(
                  {
                    defaultMessage: "Your Escalations: {numAssignments}",
                    description:
                      "Alert for the number of escalations assigned to the user",
                  },
                  { numAssignments: inbox.stats.numAssignments }
                ),
                icon:
                  inbox.stats.numAssignments > 0 ? (
                    <CircleNotifications color="error" sx={{ width: "18px" }} />
                  ) : (
                    <CheckCircle color="success" sx={{ width: "18px" }} />
                  ),
              },
            ]}
            goButton={{ linkHref: subaccountLink(`/inboxes/${inbox.id}`) }}
          />
        ))}
        {generalInboxes.map(inbox => (
          <InboxListSection
            key={`inbox-${inbox.id}`}
            title={inbox.inboxName}
            alerts={[
              ...(inbox.availableTexterSend
                ? [
                    {
                      key: "available-texter-sends",
                      text: intl.formatMessage({
                        defaultMessage: "Texter sends available",
                        description: "Alert for texter sends available",
                      }),
                    },
                  ]
                : []),
              {
                key: "available-replies",
                text: intl.formatMessage(
                  {
                    defaultMessage: "Available Replies: {numRepliesAvailable}",
                    description:
                      "Alert for the number of available replies in an inbox",
                  },
                  { numRepliesAvailable: inbox.stats.numRepliesAvailable }
                ),
              },
              {
                key: "your-replies",
                text: intl.formatMessage(
                  {
                    defaultMessage: "Your Replies: {numAssignments}",
                    description:
                      "Alert for the number of replies assigned to the user",
                  },
                  { numAssignments: inbox.stats.numAssignments }
                ),
                icon:
                  inbox.stats.numAssignments > 0 ? (
                    <CircleNotifications color="error" sx={{ width: "18px" }} />
                  ) : (
                    <CheckCircle color="success" sx={{ width: "18px" }} />
                  ),
              },
            ]}
            goButton={{ linkHref: subaccountLink(`/inboxes/${inbox.id}`) }}
          />
        ))}
        <InboxListSection
          title={intl.formatMessage({
            defaultMessage: "Your Campaigns",
            description: "Header for campaign inbox list",
          })}
          updatedAt={inboxesUpdatedAt ?? undefined}
          alerts={
            campaignInboxes.length > 0
              ? [
                  {
                    key: "campaigns",
                    text: intl.formatMessage(
                      {
                        defaultMessage: "Campaigns: {numCampaigns}",
                        description: "Alert for the number of campaigns",
                      },
                      { numCampaigns: campaignInboxes.length }
                    ),
                  },
                  {
                    key: "replies",
                    text: intl.formatMessage(
                      {
                        defaultMessage: "Replies: {numReplies}",
                        description: "Alert for the number of replies",
                      },
                      { numReplies: totalCampaignAssignedReplies }
                    ),
                    icon:
                      totalCampaignAssignedReplies > 0 ? (
                        <CircleNotifications
                          color="error"
                          sx={{ width: "18px" }}
                        />
                      ) : (
                        <CheckCircle color="success" sx={{ width: "18px" }} />
                      ),
                  },
                ]
              : []
          }
        >
          {isNarrow ? (
            <>
              <Divider sx={{ py: 1 }} />
              <List disablePadding>
                {campaignInboxes.map(inbox => {
                  return (
                    <CampaignListItem
                      startedAt={inbox.campaign.startedAt}
                      name={inbox.campaign.name}
                      description={inbox.campaign.description}
                      repliesAssigned={inbox.stats.numRepliesAssigned}
                      repliesAvailable={inbox.stats.numRepliesAvailable}
                      replyControls={
                        <CampaignInboxReplyControls
                          inbox={inbox}
                          handleEnqueueTexterSend={handleEnqueueTexterSend}
                          handleReleaseAssignments={handleReleaseAssignments}
                          handleRequestAssignmentBatch={
                            handleRequestAssignmentBatch
                          }
                          enqueuingTexterSendForCampaignId={
                            enqueuingTexterSendForCampaignId
                          }
                        />
                      }
                    />
                  );
                })}
              </List>
              {campaignInboxes.length === 0 && (
                <FlexBox sx={{ pt: 2 }}>
                  <Typography variant="body2">
                    <FormattedMessage
                      defaultMessage="You aren't assigned to any campaigns."
                      description="Empty state message for a texter with no campaigns"
                    />
                  </Typography>
                </FlexBox>
              )}
            </>
          ) : (
            <InboxCampaignsTable
              inboxes={campaignInboxes}
              inboxType={InboxType.Campaign}
              noFooter
              renderCampaignColumn={inbox => {
                return (
                  <InboxCampaignSummary
                    campaign={inbox.campaign}
                    showTexterSendInfo={true}
                  />
                );
              }}
              renderAvailableRepliesColumn={inbox => (
                <Typography>
                  {inbox.stats.numRepliesAvailable.toLocaleString()}
                </Typography>
              )}
              renderYourRepliesColumn={inbox => (
                <Typography>
                  {inbox.stats.numRepliesAssigned.toLocaleString()}
                </Typography>
              )}
              renderActionsColumn={inbox => (
                <CampaignInboxReplyControls
                  inbox={inbox}
                  handleEnqueueTexterSend={handleEnqueueTexterSend}
                  handleReleaseAssignments={handleReleaseAssignments}
                  handleRequestAssignmentBatch={handleRequestAssignmentBatch}
                  enqueuingTexterSendForCampaignId={
                    enqueuingTexterSendForCampaignId
                  }
                />
              )}
            />
          )}
          <FlexBox
            justifyContent="flex-end"
            sx={{
              marginTop: { narrow: 3, wide: 1 },
              display: totalCampaignAssignedReplies === 0 ? "none" : "flex",
            }}
          >
            {isNarrow ? (
              <Button
                onClick={() => handleReleaseAssignments({})}
                color="accessory"
                variant="contained"
                sx={{ textWrap: "wrap", lineHeight: 1.4, height: "auto" }}
              >
                <FormattedMessage
                  defaultMessage="Release All My Replies For The Day"
                  description="Button text for releasing all replies"
                />
              </Button>
            ) : (
              <LinkButton
                onClick={() => handleReleaseAssignments({})}
                variant="body3"
              >
                <FormattedMessage
                  defaultMessage="Release All My Threads"
                  description="Button text for releasing all replies"
                />
              </LinkButton>
            )}
          </FlexBox>
        </InboxListSection>
      </FlexBox>
    </PageContainer>
  );
}

export function Alerts({
  alerts,
}: {
  alerts?: { key: string; text: ReactNode; icon?: ReactNode }[];
}): ReactElement {
  const isNarrow = useMediaQuery(theme.breakpoints.down("wide"));

  return (
    <>
      {alerts?.map(alert => (
        <FlexBox key={alert.key} gap="6px" alignItems="center">
          <Typography variant={isNarrow ? "body2Bold" : "body1Bold"}>
            {alert.text}
          </Typography>
          {alert.icon}
        </FlexBox>
      ))}
    </>
  );
}

export function InboxListSection({
  title,
  updatedAt,
  label,
  alerts,
  goButton,
  children,
}: {
  title: string;
  updatedAt?: Date;
  label?: string;
  alerts?: { key: string; text: ReactNode; icon?: ReactNode }[];
  goButton?: { linkHref: string };
  children?: ReactNode;
}): ReactElement {
  const isNarrow = useMediaQuery(theme.breakpoints.down("wide"));

  return (
    <Box
      sx={{
        backgroundColor: palette.white,
        boxShadow: `0px 1px 5px 0px #6A678333`,
        position: "relative",
        padding: {
          narrow: 3,
          wide: "32px 80px",
        },
      }}
      component="section"
    >
      <FlexBox justifyContent="space-between" alignItems="center">
        <FlexBox
          justifyContent="space-between"
          sx={{
            width: "100%",
            flexDirection: {
              narrow: "column",
              wide: "row",
            },
            gap: {
              narrow: 1,
              wide: 4,
            },
          }}
        >
          <Box
            sx={{
              overflowWrap: "anywhere",
            }}
          >
            {label && (
              <Chip
                sx={{
                  height: "15px",
                  borderRadius: 0,
                  backgroundColor: palette.lightestOrange,
                  margin: 0,
                  "& .MuiChip-label": { padding: "0 4px" },
                  position: "absolute",
                  top: { narrow: "10px", wide: "18px" },
                }}
                label={<Typography variant="h4Caps">{label}</Typography>}
              />
            )}
            <Typography variant={isNarrow ? "h2Bold" : "h0Bold"} component="h2">
              {title}
            </Typography>
            {updatedAt && (
              <Typography variant="body3">
                <FormattedMessage
                  defaultMessage="Last updated: {updatedAt}"
                  description="Last updated text"
                  values={{
                    updatedAt: formatDateTime(updatedAt.getTime()),
                  }}
                />
              </Typography>
            )}
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              alignItems: { narrow: "start", wide: "center" },
              display: "flex",
              flexDirection: {
                narrow: "column",
                wide: "row",
              },
              gap: {
                narrow: 0,
                wide: 3,
              },
            }}
          >
            <Alerts alerts={alerts} />
          </Box>
        </FlexBox>
        {goButton && (
          <IconButton
            href={goButton.linkHref}
            aria-label={`View ${title}`}
            sx={{
              position: { wide: "absolute" },
              right: { narrow: "-8px", wide: "24px" },
            }}
          >
            <ChevronRight
              sx={{ fontSize: { narrow: "1.5em", wide: "unset" } }}
            />
          </IconButton>
        )}
      </FlexBox>
      {children}
    </Box>
  );
}

// For displaying campaigns in list, instead of tabular, form. Intended for mobile use.
export function CampaignListItem({
  startedAt,
  name,
  description,
  repliesAvailable,
  repliesAssigned,
  replyControls,
}: {
  startedAt: string;
  name?: string;
  description?: string | null;
  repliesAvailable: number;
  repliesAssigned: number;
  replyControls: ReactElement;
}): ReactElement {
  const intl = useIntl();
  const date = new Date(startedAt);
  const dateString = `Started: ${date.toLocaleDateString()} at ${date.toLocaleTimeString([], { hour12: true, hour: "numeric", minute: "numeric" })}`;
  const alerts = [
    {
      key: "available-replies",
      text: intl.formatMessage(
        {
          defaultMessage: "Available Replies: {repliesAvailable}",
          description: "Alert for the number of available replies in an inbox",
        },
        { repliesAvailable: repliesAvailable }
      ),
    },
    {
      key: "your-replies",
      text: intl.formatMessage(
        {
          defaultMessage: "Your Replies: {repliesAssigned}",
          description: "Alert for the number of replies assigned to the user",
        },
        { repliesAssigned: repliesAssigned }
      ),
      icon:
        repliesAssigned > 0 ? (
          <CircleNotifications color="error" sx={{ width: "18px" }} />
        ) : (
          <CheckCircle color="success" sx={{ width: "18px" }} />
        ),
    },
  ];

  return (
    <ListItem divider={true} disableGutters={true} sx={{ py: 3 }}>
      <FlexBox direction="column" gap={2} sx={{ width: "100%" }}>
        <FlexBox direction="column" gap={1}>
          <Typography variant="h3" fontWeight={600} fontSize="1em">
            {name || "No associated campaign"}
          </Typography>
          {description && (
            <Typography variant="body2">{description}</Typography>
          )}
          {startedAt && <Typography variant="body2">{dateString}</Typography>}
        </FlexBox>
        <FlexBox justifyContent="space-between" gap={2} alignItems="flex-start">
          <Box sx={{ pt: "4px" }}>
            <Alerts alerts={alerts} />
          </Box>
          {replyControls}
        </FlexBox>
      </FlexBox>
    </ListItem>
  );
}

type InboxCampaignsTableProps<T extends { id: string }> = {
  inboxes: T[];
  inboxType: InboxType;
  renderCampaignColumn: (inbox: T) => ReactElement;
  renderAvailableRepliesColumn: (inbox: T) => ReactElement;
  renderYourRepliesColumn: (inbox: T) => ReactElement;
  renderActionsColumn: (inbox: T) => ReactElement;
} & (
  | Omit<TablePropsWithPaginationFooter<T>, "rows" | "columns">
  | Omit<TablePropsWithCustomFooter<T>, "rows" | "columns">
  | Omit<TablePropsWithoutFooter<T>, "rows" | "columns">
);

export function InboxCampaignsTable<
  T extends { id: string } & (
    | CampaignInboxSummary
    | {
        statsByCampaign: {
          availableCount: number;
          assignmentCount: number;
          campaign?: { id?: string; name?: string } | null;
        };
      }
  ),
>(props: InboxCampaignsTableProps<T>): ReactElement {
  const {
    inboxes,
    inboxType,
    renderCampaignColumn,
    renderAvailableRepliesColumn,
    renderYourRepliesColumn,
    renderActionsColumn,
    ...tableProps
  } = props;
  const intl = useIntl();

  return (
    <Table
      emptyState={
        <FormattedMessage
          defaultMessage="You aren't assigned to any campaigns."
          description="Empty state message for a texter with no campaigns"
        />
      }
      rows={inboxes}
      columns={[
        {
          key: "campaign",
          title: intl.formatMessage({
            defaultMessage: "Campaign",
            description: "Header for campaign column",
          }),
          renderValue: renderCampaignColumn,
        },
        {
          key: "availableReplies",
          title:
            inboxType === InboxType.Escalation
              ? intl.formatMessage({
                  defaultMessage: "Available Escalations",
                  description: "Header for available escalations column",
                })
              : intl.formatMessage({
                  defaultMessage: "Available Replies",
                  description: "Header for available escalations column",
                }),
          renderValue: renderAvailableRepliesColumn,
        },
        {
          key: "yourReplies",
          title:
            inboxType === InboxType.Escalation
              ? intl.formatMessage({
                  defaultMessage: "Your Escalations",
                  description: "Header for the your replies column",
                })
              : intl.formatMessage({
                  defaultMessage: "Your Replies",
                  description: "Header for the your replies column",
                }),
          renderValue: renderYourRepliesColumn,
        },
        {
          key: "actions",
          title: intl.formatMessage({
            defaultMessage: "Actions",
            description: "Header for actions column",
          }),
          renderValue: renderActionsColumn,
        },
      ]}
      containerSx={{ overflowX: "visible" }}
      {...tableProps}
    />
  );
}
